import { createApp, defineComponent, h } from 'vue'
import App from './App.vue'
import router from './router'
import { createBootstrap } from 'bootstrap-vue-next/plugins/createBootstrap'
import { BAlert } from 'bootstrap-vue-next/components/BAlert'
import { BButton } from 'bootstrap-vue-next/components/BButton'
import { BForm } from 'bootstrap-vue-next/components/BForm'
import { BFormCheckbox, BFormCheckboxGroup } from 'bootstrap-vue-next/components/BFormCheckbox'
import { BFormFile } from 'bootstrap-vue-next/components/BFormFile'
import { BFormGroup } from 'bootstrap-vue-next/components/BFormGroup'
import { BFormInput } from 'bootstrap-vue-next/components/BFormInput'
import { BFormSelect } from 'bootstrap-vue-next/components/BFormSelect'
import { BFormTextarea } from 'bootstrap-vue-next/components/BFormTextarea'
import { BNavItem } from 'bootstrap-vue-next/components/BNav'
import { BNavbar, BNavbarBrand, BNavbarNav } from 'bootstrap-vue-next/components/BNavbar'
import { BPagination } from 'bootstrap-vue-next/components/BPagination'
import { BProgress } from 'bootstrap-vue-next/components/BProgress'
import { BSpinner } from 'bootstrap-vue-next/components/BSpinner'

import 'bootstrap-vue-next/dist/bootstrap-vue-next.css'
import 'bootstrap-icons/font/bootstrap-icons.css'
import '@fontsource/inter/400.css'
import '@fontsource/inter/500.css'
import '@fontsource/inter/600.css'
import '@fontsource/inter/700.css'

import './style/theme.css'
import './style/design-tokens.css'
import './style/accessibility.css'
import './style/tour.css'

import keycloakPlugin from '@/plugins/keycloak-plugin'
import themeService from '@/services/ThemeService'
import notificationService from '@/services/NotificationService'
import { getRuntimeConfig, validateRuntimeConfig } from '@/services/runtimeConfig'
import { installToastedCompat } from '@/plugins/toasted-compat'
import { installBvModalCompat } from '@/plugins/bvmodal-compat'
import { registerKeycloak, requireAuthenticatedRoute } from '@/services/authManager'
import { registerUiCompat } from '@/services/uiCompatFacade'

// Initialize theme early to prevent flash of wrong theme
themeService.init()

// Initialize notification service
notificationService.init()

const hideBootScreen = () => {
  const bootScreen = document.getElementById('mf-boot-screen')
  if (!bootScreen) return

  bootScreen.classList.add('mf-hidden')
  setTimeout(() => {
    if (bootScreen && bootScreen.parentNode) {
      bootScreen.parentNode.removeChild(bootScreen)
    }
  }, 250)
}

const config = getRuntimeConfig()
validateRuntimeConfig()

const keycloakConfig = {
  url: config.KEYCLOAK_URL,
  realm: config.KEYCLOAK_REALM,
  clientId: config.KEYCLOAK_CLIENT_ID
}

const BIconCompat = defineComponent({
  name: 'BIcon',
  inheritAttrs: false,
  props: {
    icon: {
      type: String,
      default: ''
    },
    fontScale: {
      type: [String, Number],
      default: null
    }
  },
  setup (props, { attrs, slots }) {
    return () => {
      const iconName = String(props.icon || '').trim()
      const style = { ...(attrs.style || {}) }
      if (props.fontScale !== null && props.fontScale !== undefined) {
        const scale = Number(props.fontScale)
        if (!Number.isNaN(scale) && scale > 0) {
          style.fontSize = `${scale}em`
        }
      }
      return h(
        'i',
        {
          ...attrs,
          class: [attrs.class, 'bi', iconName ? `bi-${iconName}` : null],
          style,
          'aria-hidden': attrs['aria-hidden'] ?? 'true'
        },
        slots.default ? slots.default() : []
      )
    }
  }
})

const legacyBIconNames = [
  'arrow-counterclockwise', 'arrow-left', 'arrow-repeat', 'arrow-right', 'arrow-right-square',
  'bar-chart', 'bar-chart-fill', 'bell', 'bell-fill', 'calendar-check',
  'chat-square-text', 'check', 'check-circle-fill', 'chevron-left', 'chevron-right',
  'clipboard', 'clock', 'clock-history', 'collection', 'display', 'download',
  'exclamation-triangle-fill', 'eye', 'file-earmark-text', 'file-text', 'folder',
  'folder-fill', 'folder-x', 'gear', 'gear-fill', 'globe', 'grid-3x3-gap',
  'house-fill', 'image', 'info-circle-fill', 'lightbulb', 'link', 'list-ul',
  'lock', 'lock-fill', 'mic-fill', 'moon-fill', 'music-note-beamed', 'palette-fill',
  'pause-fill', 'pencil', 'people', 'people-fill', 'person', 'person-fill', 'play-fill', 'plus',
  'plus-circle', 'plus-circle-fill', 'plus-lg', 'question-circle', 'search', 'send',
  'shield-fill-x', 'shield-lock-fill', 'shield-x', 'sort-alpha-down',
  'sort-alpha-up', 'sort-down', 'speedometer2', 'stack', 'stars', 'stop-fill',
  'stopwatch', 'sun-fill', 'three-dots-vertical', 'toggle-off', 'toggle-on',
  'trash', 'upload', 'volume-up', 'volume-up-fill', 'x', 'x-circle-fill', 'x-lg'
]

const bootstrapComponents = {
  BAlert,
  BButton,
  BForm,
  BFormCheckbox,
  BFormCheckboxGroup,
  BFormFile,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormTextarea,
  BNavItem,
  BNavbar,
  BNavbarBrand,
  BNavbarNav,
  BPagination,
  BProgress,
  BSpinner
}

const toPascalCase = (kebabName) => kebabName
  .split('-')
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
  .join('')

const createLegacyIconAlias = (iconName) => defineComponent({
  name: `BIcon${toPascalCase(iconName)}`,
  inheritAttrs: false,
  props: {
    fontScale: {
      type: [String, Number],
      default: null
    }
  },
  setup (props, { attrs, slots }) {
    return () => h(
      BIconCompat,
      { ...attrs, icon: iconName, fontScale: props.fontScale },
      slots
    )
  }
})

function mountApp(keycloak) {
  const app = createApp(App)

  // Legacy b-icon compatibility (BootstrapVue v2 -> Vue 3 migration).
  app.component('BIcon', BIconCompat)
  legacyBIconNames.forEach((iconName) => {
    app.component(`BIcon${toPascalCase(iconName)}`, createLegacyIconAlias(iconName))
  })

  // Register only the BootstrapVueNext subset actually used by the app to
  // reduce vendor weight while keeping legacy templates functional.
  Object.entries(bootstrapComponents).forEach(([name, component]) => {
    app.component(name, component)
  })

  app.use(router)
  app.use(createBootstrap({
    breadcrumb: false,
    popover: false,
    rtl: false,
    tooltip: false,
    toast: false
  }))
  const toasted = installToastedCompat(app, { timeout: 3000, position: 'bottom-right' })
  const bvModal = installBvModalCompat(app)
  registerUiCompat({ toasted, bvModal })

  if (keycloak) {
    registerKeycloak(keycloak)
  }

  router.beforeEach(async (to) => {
    try {
      const result = await requireAuthenticatedRoute(to)
      if (!result.allowed) {
        return result.redirectTo || false
      }
      return true
    } catch (err) {
      console.error('Erreur de validation de la route :', err)
      return '/'
    }
  })

  app.config.errorHandler = (err, _instance, info) => {
    console.error('[Vue] Erreur non gérée :', info, err)
    toasted?.error?.('Une erreur inattendue est survenue.')
  }

  app.mount('#app')
  hideBootScreen()
}

keycloakPlugin.initialize({ config: keycloakConfig }).then((keycloak) => {
  mountApp(keycloak)
}).catch((err) => {
  console.error('[Keycloak] Echec initialisation, montage en mode dégradé :', err)
  mountApp(null)
})

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
  })
}
